import React from 'react';
import { Monitor, BatteryLow, Folder, Building2 } from 'lucide-react';
import { Unit, Group } from '../../types';
import StatCard from '../ui/StatCard';
import { BatteryDonut, LocksPerGroupBars } from './Charts';
import LocksMap from './LocksMap';
import { DashboardSection } from './DashboardLayout';

interface OverviewSectionProps {
  units: Unit[];
  groups: Group[];
  companiesCount: number;
  companyUsersCount: number;
  onNavigate: (s: DashboardSection) => void;
}

const Panel: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col ${className || ''}`}>
    <div className="px-5 py-4 border-b border-slate-100">
      <h2 className="text-sm font-bold text-slate-700">{title}</h2>
    </div>
    <div className="flex-1 p-4 min-h-0">{children}</div>
  </div>
);

const OverviewSection: React.FC<OverviewSectionProps> = ({
  units,
  groups,
  companiesCount,
  companyUsersCount,
  onNavigate,
}) => {
  const lowBattery = units.filter((u) => (u.battery ?? 0) < 20).length;

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Monitor} accent="blue" label="Total locks" value={units.length} onClick={() => onNavigate('locks')} />
        <StatCard
          icon={BatteryLow}
          accent={lowBattery > 0 ? 'red' : 'emerald'}
          label="Low battery"
          value={lowBattery}
          hint="< 20%"
          onClick={() => onNavigate('locks')}
        />
        <StatCard icon={Folder} accent="violet" label="Groups" value={groups.length} onClick={() => onNavigate('groups')} />
        <StatCard
          icon={Building2}
          accent="amber"
          label="Companies"
          value={companiesCount}
          hint={`${companyUsersCount} company users`}
          onClick={() => onNavigate('companies')}
        />
      </div>

      {/* Map + battery donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel title="Lock locations" className="lg:col-span-2 h-[420px]">
          <LocksMap units={units} />
        </Panel>
        <Panel title="Battery status" className="h-[420px]">
          <BatteryDonut units={units} />
        </Panel>
      </div>

      {/* Locks per group */}
      <Panel title="Locks per group" className="h-72">
        <LocksPerGroupBars groups={groups} />
      </Panel>
    </div>
  );
};

export default OverviewSection;
